<template>
  <div class="fts-console" :class="{ mh: vectorId }">
    <!-- module Card -->
    <div class="console-card console-card-module">
      <!-- header -->
      <div
        class="console-card-header"
        :class="{ 'tour-console-create-module': !vectorId }"
        ref="moduleCardHeader"
      >
        <!-- name -->
        <div class="console-card-module-info">
          <!-- emoji -->
          <fts-emoji
            :appendToBody="true"
            :toggleSize="35"
            v-model="vector.emoji"
            class="console-card-module-emoji"
          />
          <!-- name -->
          <fts-contenteditable
            v-model="vector.name"
            placeholder="Please enter module name"
            class="console-card-module-name"
            :key="vectorId"
            @input="debounceInput"
            ref="moduleName"
          />
          <!-- profile -->
          <div class="console-card-module-tag" v-if="vector.isUserCollection">
            <font-awesome-icon slot="trigger" :icon="['fas', 'user']" fixed-width />
          </div>
        </div>
        <!-- description -->
        <div class="console-card-description">
          {{ card.description }}.
          <a
            :href="card.learnMore"
            target="_blank"
            class="console-card-description-link"
            v-if="!!card.learnMore"
          >Learn more</a>
        </div>
        <!-- description -->
        <fts-contenteditable
          v-model="vector.description"
          placeholder="enter module description"
          class="console-card-module-description"
        />
        <!-- toggle -->
        <div class="console-card-toggle" v-if="vectorId">
          <!-- user collection -->
          <fts-toggle-switch
            :form-data="vector"
            :item="{ fieldName: 'isUserCollection' }"
            :size="20"
            color="primary"
            mat-class
            label-text="User Profile"
            label-dir="left"
          >
            <div
              slot="label"
              slot-scope="{label, labelStyle, defaultClass}"
              class="console-card-toggle-label"
              :class="defaultClass"
              :style="labelStyle"
            >
              <div class="module-tag">
                <font-awesome-icon :icon="['fas', 'user']" fixed-width />
              </div>
              {{label}}
              <fts-tippy
                content="Connects this module to users. Able to manage user access or permission through profile."
              >
                <font-awesome-icon
                  slot="trigger"
                  :icon="['fas', 'info-circle']"
                  class="label-helper"
                />
              </fts-tippy>
            </div>
          </fts-toggle-switch>
          <!-- publish -->
          <fts-toggle-switch
            :form-data="vector"
            :item="{ fieldName: 'published' }"
            :size="20"
            color="primary"
            mat-class
            label-text="Publish"
            label-dir="left"
          >
            <div
              slot="label"
              slot-scope="{label, labelStyle, defaultClass}"
              class="console-card-toggle-label"
              :class="defaultClass"
              :style="labelStyle"
            >
              {{label}}
              <fts-tippy content="Set module to publish or unpublish.">
                <font-awesome-icon
                  slot="trigger"
                  :icon="['fas', 'info-circle']"
                  class="label-helper"
                />
              </fts-tippy>
            </div>
          </fts-toggle-switch>
        </div>
      </div>
      <!-- body -->
      <div class="console-card-body">
        <!-- edit -->
        <template v-if="vectorId">
          <div class="console-card-content">
            <div>
              <!-- automation -->
              <div
                class="console-card-automation-container"
                v-if="
                  getAutomation(card.name) &&
                  getAutomation(card.name).length
                "
              >
                <fts-event
                  v-for="event in getAutomation(card.name)"
                  :key="event.id"
                  :event="event"
                  @open="showAutomationDesignModal"
                />
              </div>
              <!-- audit -->
              <div class="console-card-audit" v-if="vector.audit && vector.audit.updatedBy">
                <div class="console-card-audit-by">last updated by</div>
                <fts-profile :user-name="vector.audit.updatedBy" />
                <div
                  class="console-card-audit-date"
                >{{moment(vector.audit.updatedDate).format('lll')}}</div>
              </div>
            </div>
            <!-- actions -->
            <div class="console-card-content-actions">
              <!-- notification -->
              <fts-event-toggle
                event-type="notification"
                class="console-card-event-btn"
                @open="addAutomation(card, 'notification')"
              />
              <!-- automation -->
              <fts-event-toggle
                class="console-card-event-btn"
                @open="addAutomation(card, 'automation')"
              />
              <!-- authorization -->
              <fts-auth type="vector">
                <fts-btn
                  slot="trigger"
                  slot-scope="{ show, isRight }"
                  icon
                  text
                  :color="isRight ? 'danger' : 'var(--fts-gray-5)'"
                  @click="show"
                >
                  <font-awesome-icon :icon="['fas', 'lock']" v-if="isRight" />
                  <font-awesome-icon :icon="['fas', 'unlock']" v-else />
                </fts-btn>
              </fts-auth>
              <!-- delete -->
              <fts-btn icon text color="var(--fts-gray-5)" @click="deleteModule">
                <font-awesome-icon :icon="['far', 'trash-alt']" />
              </fts-btn>
              <!-- share -->
              <fts-btn icon text color="var(--fts-gray-5)" @click="packModule" v-if="isInternal">
                <font-awesome-icon :icon="['fas', 'share-alt']" />
              </fts-btn>
              <!-- notification -->
              <fts-btn
                icon
                text
                :color="showNotificationPanel ? 'primary' : 'var(--fts-gray-5)'"
                @click="toggleNotificationPanel"
                v-if="isInternal"
              >
                <font-awesome-icon :icon="['far', 'bell']" />
              </fts-btn>
              <!-- setting -->
              <fts-btn
                icon
                text
                :color="moduleAdvancedSettings ? 'primary' : 'var(--fts-gray-5)'"
                @click="toggleModuleSettings"
                v-if="beta"
              >
                <font-awesome-icon :icon="['fas', 'cog']" />
              </fts-btn>
            </div>
          </div>
          <!-- notification panel -->
          <fts-transition-expand>
            <div
              class="console-card-module-form"
              :class="{ 'form-divider': showNotificationPanel }"
              v-if="vectorId && showNotificationPanel"
            >
              <notification-panel :module="vectorId" />
            </div>
          </fts-transition-expand>
          <!-- settings -->
          <fts-transition-expand>
            <div
              class="fts-row console-card-module-form"
              :class="{ 'form-divider': moduleAdvancedSettings }"
              v-if="vectorId && moduleAdvancedSettings"
            >
              <!-- depository -->
              <div class="fts-col">
                <div class="fts-row gutters">
                  <div class="fts-col fts-col-md-6 fts-form-group">
                    <div class="fts-form-label">Workspace</div>
                    <fts-select
                      label="name"
                      :reduce="(item) => item"
                      :filterable="false"
                      :options="workspaceOptions"
                      v-model="moduleWorkspace"
                      @search="fetchWorkspaceOptions"
                      @search:focus="preloadWorkspaces"
                    />
                  </div>
                </div>
              </div>
              <!-- miscellaneous -->
              <div class="fts-col">
                <div class="fts-row gutters">
                  <div class="fts-col fts-col-md-6 fts-form-group">
                    <div class="fts-form-label">Workspaces</div>
                    <fts-select
                      label="name"
                      :reduce="(item) => item"
                      :filterable="false"
                      multiple
                      :options="workspaceOptions"
                      v-model="vector.workspaces"
                      @search="fetchWorkspaceOptions"
                      @search:focus="preloadWorkspaces"
                    />
                  </div>

                  <div class="fts-col fts-col-md-6 fts-form-group">
                    <div class="fts-form-label">Version</div>
                    <input type="text" class="fts-form-control" v-model="vector.version" />
                  </div>
                </div>
              </div>
            </div>
          </fts-transition-expand>
        </template>
        <!-- create -->
        <template v-else>
          <div class="console-card-create-action fts-text-right">
            <fts-btn text :disabled="submitting" @click="cancelCreateModule">Cancel</fts-btn>
            <fts-btn
              color="secondary"
              box-shadow-none
              :loading="submitting"
              @click="createModule"
            >Create Module</fts-btn>
          </div>
        </template>
      </div>
    </div>
    <!-- content -->
    <template v-if="vectorId">
      <template v-if="information && information.length">
        <!-- search -->
        <div class="console-search" ref="consoleSearchInput">
          <fts-console-search-input
            v-model="searchKeyword"
            placeholder="Type something to create or to search"
            @input:search="filter"
            @input:clear="clearFilter"
            @keydown:tab="switchSuggest"
            @keydown:enter="submitSuggest"
            ref="searchKeyword"
          />
        </div>
        <!-- suggestion -->
        <div id="searchSuggestion" class="console-suggest">
          <div class="console-suggest-content" v-if="suggestions && suggestions.length">
            <div class="console-suggest-title">Suggestions</div>
            <div class="console-suggest-desc">Press enter to select. Press tab to switch.</div>

            <div class="console-suggest-results">
              <div
                class="console-suggest-result"
                v-for="(x, ind) in suggestions"
                :key="x.id"
                @mouseover="setSuggesMouseHover(x, true)"
                @mouseleave="setSuggesMouseHover(x)"
              >
                <div
                  class="console-suggest-card"
                  :class="{ focused: ind === focusSuggest }"
                  @click="selectSuggest(ind)"
                >
                  <div class="console-suggest-card-header">
                    <font-awesome-layers class="console-suggest-card-check">
                      <font-awesome-icon :icon="['far', 'circle']" />
                      <font-awesome-icon
                        :icon="['fas', 'check']"
                        transform="shrink-8"
                        v-if="false"
                      />
                    </font-awesome-layers>
                    <div class="console-suggest-card-title">{{ x.section }}</div>
                  </div>
                  <div class="console-suggest-card-body">
                    <!-- information -->
                    <div class="console-suggest-element" v-if="x.section == 'information'">
                      <i
                        class="console-suggest-element-icon fts-text-secondary"
                        :class="informationIcon(x.type)"
                      />
                      <div class="console-suggest-element-name">{{ x.displayName }}</div>
                    </div>
                    <!-- activity -->
                    <div class="console-suggest-element" v-else-if="x.section == 'activity'">
                      <fts-moji :emoji="x.emoji" :size="19" class="suggest-card-icon" />
                      <div class="console-suggest-element-name">{{ x.name }}</div>
                    </div>
                    <!-- state -->
                    <fts-state
                      :state="{ name: x.name, color: x.color }"
                      class="console-suggest-state"
                      v-else-if="x.section == 'state'"
                    />
                    <!-- actions -->
                    <fts-btn
                      sm
                      icon
                      color="primary"
                      class="console-suggest-card-action"
                      @click.native.prevent="editSuggest(x)"
                      v-if="x.mouseHover"
                    >
                      <font-awesome-icon :icon="['fas', 'edit']" />
                    </fts-btn>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <!-- cards -->
      <div class="console-section">
        <template v-for="x in cardGroup">
          <div
            class="console-section-wrapper"
            v-if="x.group.length"
            :key="`m-${vectorId}-${x.name}`"
          >
            <template v-for="card in x.group">
              <div class="console-card" v-if="relyOnReady(card)" :key="card.name">
                <!-- header -->
                <div class="console-card-header">
                  <div class="console-card-title-wrapper">
                    <div class="console-card-title">
                      {{ card.displayName }}
                      <fts-release-badge class="fts-align-super" v-if="card.beta" />
                    </div>
                    <fts-btn
                      sm
                      outline
                      color="primary"
                      text-transform-init
                      class="console-card-action-create"
                      :class="`tour-create-${card.name}`"
                      @click="openConsoleElement(card)"
                      v-if="card.editable"
                    >
                      <font-awesome-icon :icon="['fas', 'plus']" />Add
                    </fts-btn>
                  </div>
                  <div class="console-card-description">
                    {{ card.description }}.
                    <a
                      :href="card.learnMore"
                      target="_blank"
                      class="console-card-description-link"
                      v-if="!!card.learnMore"
                    >Learn more</a>
                  </div>
                </div>
                <!-- body -->
                <div class="console-card-body">
                  <div
                    :id="`console-card-element-${card.name}`"
                    class="console-card-element fts-scrollbar"
                  >
                    <fts-console-element
                      :parent-id="`console-card-element-${card.name}`"
                      :canvas="canvas"
                      :item="card.fieldName"
                      :type="card.name"
                      :label="card.displayName"
                      :page-size="card.pageSize"
                      :draggable="card.draggable"
                      :editable="card.editable"
                      :authorization="card.authorization"
                      :automation="card.automation"
                      @update="openConsoleElement(card, $event)"
                      @openAutomation="showAutomationDesignModal"
                      @openSubscription="openSubscriptionDesignModal"
                      @moduleSelected="$emit('module:selected', $event)"
                    />
                    <!-- system default -->
                    <fts-console-element
                      :canvas="systemDefault"
                      :item="card.fieldName"
                      :type="card.name"
                      :label="card.displayName"
                      :draggable="false"
                      :editable="false"
                      :authorization="false"
                      :automation="false"
                      system
                      @update="openConsoleElement(card, $event)"
                      @openAutomation="showAutomationDesignModal"
                      @openSubscription="openSubscriptionDesignModal"
                      @moduleSelected="$emit('module:selected', $event)"
                      v-if="card.name === 'information'"
                    />
                  </div>
                  <div class="console-card-body__footer" v-if="card.automation && beta">
                    <fts-release-badge />
                    <div class="fts-inline-spacer-5"></div>
                    <!-- automation -->
                    <div
                      class="console-card-automation-container"
                      v-if="
                        getAutomation(card.fieldName) &&
                        getAutomation(card.fieldName).length
                      "
                    >
                      <fts-event
                        v-for="event in getAutomation(card.fieldName)"
                        :key="event.id"
                        :event="event"
                        @open="showAutomationDesignModal"
                      />
                    </div>
                    <!-- actions -->
                    <div class="console-card-content-actions">
                      <!-- notification -->
                      <fts-event-toggle
                        event-type="notification"
                        class="console-card-event-btn"
                        @open="addAutomation(card, 'notification')"
                      />
                      <!-- automation -->
                      <fts-event-toggle
                        class="console-card-event-btn"
                        @open="addAutomation(card, 'automation')"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </template>
      </div>
      <!-- no found -->
      <div class="console-no-result" v-if="!filterCard.length">
        <div class="no-result-label">No results found</div>
        <div class="no-result-desc">Try different search term</div>
      </div>
    </template>
    <!-- pack module modal -->
    <fts-pack-module-modal ref="ftsPackModuleModal" />
    <!-- information design modal -->
    <fts-information-design-modal ref="ftsInformationDesignModal" />
    <!-- document design modal -->
    <fts-document-design-modal ref="ftsDocumentDesignModal" />
    <!-- activity design modal -->
    <fts-activity-design-modal ref="ftsActivityDesignModal" />
    <!-- standard activity design modal -->
    <fts-standard-activity-design-modal ref="ftsStandardActivityDesignModal" />
    <!-- listing design modal -->
    <fts-listing-design-modal ref="listingDesign" />
    <!-- state design modal -->
    <fts-state-design-modal ref="ftsStateDesignModal" />
    <!-- flow design modal -->
    <fts-flow-design-modal ref="ftsFlowDesignModal" />
    <!-- action design modal -->
    <fts-action-design-modal ref="ftsActionDesignModal" />
    <!-- automation design modal -->
    <fts-automation-design-modal ref="ftsAutomationDesignModal" />
    <!-- logic service design modal -->
    <fts-logic-service-design-modal ref="ftsLogicServiceDesignModal" />
    <!-- import design modal -->
    <fts-import-design-modal ref="ftsImportDesignModal" />
    <!-- subscription design modal -->
    <fts-subscription-design-modal ref="ftsSubscriptionDesignModal" />
  </div>
</template>

<script>
import Vue from "vue";
import moment from "moment";

import { authorizationStore } from "../store/authorizationStore";
import commons from "../commons/commons";
import validator from "../commons/designValidator";
import canvasDesign from "../commons/canvasDesign";
import mixin from "../mixins/mixin";

import FTSTransitionExpand from "../elements/FTSTransitionExpand";
import FTSConsoleElement from "./FTSConsoleElement";
import FTSEmoji from "../elements/FTSEmoji";
import FTSContenteditable from "../elements/FTSContenteditable";
import FTSSelect from "../elements/FTSSelect";
import FTSToggleSwitch from "../elements/FTSToggleSwitch";
import FTSSpinner from "../elements/FTSSpinner";
import FTSTextArea from "../elements/FTSTextArea";
import FTSBtn from "../elements/FTSBtn";
import FTSState from "../elements/FTSState";
import FTSMoji from "../elements/FTSMoji";
import FTSAuth from "./FTSAuth";
import FTSReleaseBadge from "../elements/FTSReleaseBadge";
import FTSTippy from "../elements/FTSTippy";
import FTSProfile from "../elements/FTSProfile";
import FTSEvent from "../design/event/FTSEvent";
import FTSEventToggle from "../design/event/FTSEventToggle";

import FTSInformationDesignModal from "./FTSInformationDesignModal";
import FTSDocumentDesignModal from "./FTSDocumentDesignModal";
import FTSActivityDesignModal from "./FTSActivityDesignModal";
import FTSStandardActivityDesignModal from "./FTSStandardActivityDesignModal";
import FTSListingDesignModal from "./FTSListingDesignModal";
import FTSStateDesignModal from "./FTSStateDesignModal";
import FTSFlowDesignModal from "./FTSFlowDesignModal";
import FTSActionDesignModal from "./FTSActionDesignModal";
import FTSLogicServiceDesignModal from "./FTSLogicServiceDesignModal";
import FTSAutomationDesignModal from "./FTSAutomationDesignModal";
import FTSImportDesignModal from "./FTSImportDesignModal";
import FTSPackModuleModal from "./FTSPackModuleModal";
import FTSConsoleSearchInput from "./console/FTSConsoleSearchInput";
import FTSSubscriptionDesignModal from "./FTSSubscriptionDesignModal";

import NotificationPanel from "./../administration/NotificationControlPanel.vue";

import VueScrollTo from "vue-scrollto";
Vue.use(VueScrollTo);

import _ from "lodash";

import Vuex from "vuex";
Vue.use(Vuex);

import Vuelidate from "vuelidate";
Vue.use(Vuelidate);

import { store } from "../../components/store/canvasDesignStore";

/* assets */
import studioBg from "../../assets/images/studio/studio-bg.png";

export default {
  props: {
    canvasProps: Object,
    baseServer: String,
    vectorId: [String, Number],
    vectorProps: {
      type: Object,
      default: () => ({})
    },
    validator: Object,
    workspaceId: Number
  },
  components: {
    "fts-transition-expand": FTSTransitionExpand,
    "fts-console-element": FTSConsoleElement,
    "fts-emoji": FTSEmoji,
    "fts-contenteditable": FTSContenteditable,
    "fts-select": FTSSelect,
    "fts-toggle-switch": FTSToggleSwitch,
    "fts-spinner": FTSSpinner,
    "fts-text-area": FTSTextArea,
    "fts-btn": FTSBtn,
    "fts-state": FTSState,
    "fts-moji": FTSMoji,
    "fts-auth": FTSAuth,
    "fts-release-badge": FTSReleaseBadge,
    "fts-tippy": FTSTippy,
    "fts-profile": FTSProfile,
    "fts-event": FTSEvent,
    "fts-event-toggle": FTSEventToggle,
    /* design modal */
    "fts-information-design-modal": FTSInformationDesignModal,
    "fts-document-design-modal": FTSDocumentDesignModal,
    "fts-activity-design-modal": FTSActivityDesignModal,
    "fts-standard-activity-design-modal": FTSStandardActivityDesignModal,
    "fts-listing-design-modal": FTSListingDesignModal,
    "fts-state-design-modal": FTSStateDesignModal,
    "fts-flow-design-modal": FTSFlowDesignModal,
    "fts-action-design-modal": FTSActionDesignModal,
    "fts-logic-service-design-modal": FTSLogicServiceDesignModal,
    "fts-automation-design-modal": FTSAutomationDesignModal,
    "fts-import-design-modal": FTSImportDesignModal,
    "fts-pack-module-modal": FTSPackModuleModal,
    "fts-console-search-input": FTSConsoleSearchInput,
    "fts-subscription-design-modal": FTSSubscriptionDesignModal,
    /* notification panel */
    "notification-panel": NotificationPanel
  },
  store,
  mixins: [mixin],
  data() {
    return {
      packages: [],
      // Setup
      dragOptions: commons.dragOptions,
      card: {
        name: "vector",
        fieldName: "vector",
        displayName: "Vector",
        background: studioBg,
        learnMore: "https://community.inistate.com/t/fetias101-module/79/2",
        description:
          "module is part of your business app like customers, employees, jobs or tickets"
      },
      cards: [
        {
          name: "standardActivity",
          fieldName: "standardActivities",
          displayName: "Basic",
          background: studioBg,
          description: "actions that app commonly do",
          editable: false,
          draggable: false,
          authorization: true,
          automation: true,
          pageSize: 4,
          relyOn: [],
          learnMore: "https://community.inistate.com/t/fetias101-standard/88"
        },
        {
          name: "information",
          fieldName: "information",
          displayName: "Information",
          background: studioBg,
          description: "data or fields",
          editable: true,
          authorization: false,
          automation: true,
          relyOn: [],
          learnMore: "https://community.inistate.com/t/fetias101-information/81"
        },
        {
          name: "activity",
          fieldName: "activities",
          displayName: "Activity",
          background: studioBg,
          description: "additional actions for your team",
          editable: true,
          authorization: true,
          automation: true,
          relyOn: [],
          learnMore: "https://community.inistate.com/t/fetias101-activity/82"
        },
        {
          name: "state",
          fieldName: "states",
          displayName: "State",
          background: studioBg,
          relyOn: [],
          description: "status showing the progress",
          editable: true,
          authorization: false,
          automation: true,
          learnMore: "https://community.inistate.com/t/fetias101-state/83"
        },
        {
          name: "flow",
          fieldName: "flows",
          displayName: "Flow",
          background: studioBg,
          description: "how a state changed to another by activity",
          editable: true,
          authorization: false,
          automation: false,
          relyOn: ["state", "activity"],
          learnMore: "https://community.inistate.com/t/fetias101-flow/87"
        },

        {
          name: "menu",
          fieldName: "menus",
          displayName: "Listing",
          background: studioBg,
          description: "curated list with defined views",
          editable: true,
          authorization: true,
          automation: false,
          relyOn: ["information"],
          learnMore: "https://community.inistate.com/t/fetias101-listing/96"
        },
        {
          name: "document",
          fieldName: "documents",
          displayName: "Template",
          background: studioBg,
          description: "documents or reports",
          editable: true,
          authorization: true,
          automation: false,
          relyOn: ["information"],
          learnMore: "https://community.inistate.com/t/fetias101-template/89"
        },
        {
          name: "action",
          fieldName: "actions",
          displayName: "Custom",
          background: studioBg,
          description: "special actions",
          editable: true,
          authorization: true,
          automation: false,
          relyOn: ["information"],
          beta: true
        },
        {
          name: "logicService",
          fieldName: "logicServices",
          displayName: "Logic",
          background: studioBg,
          description: "extra boost of logics",
          editable: true,
          authorization: false,
          automation: false,
          relyOn: ["information"],
          beta: true
        }
      ],
      fileDepositoryTypes: [
        { id: 0, name: "Direct" },
        { id: 1, name: "File Transfer Protocol" },
        { id: 2, name: "Amazon Web Service S3" }
      ],
      vectorTypes: [
        { id: 0, name: "Process" },
        { id: 1, name: "Record" }
      ],
      workspaceOptions: [],
      canvas: {},
      vector: {},
      // Module (Create)
      submitting: false,
      // Module
      moduleAdvancedSettings: false,
      showNotificationPanel: false,
      // Search
      searchKeyword: null,
      focusSuggest: 0
    };
  },
  validations() {
    let vector = {};
    vector = validator.moduleDesign(this.$store.getters.workspaceModules);
    return { vector };
  },
  methods: {
    moment,
    relyOnReady(card) {
      if (!card.relyOn || card.relyOn.length == 0) {
        return true;
      }

      if (this.canvas.information && this.canvas.information.length) {
        return true;
      }

      return false;
    },
    async packModule() {
      await this.$refs.ftsPackModuleModal.show();
    },
    reset() {
      // scroll to
      this.$emit("scrollTo", { x: 0, y: 0 });
      // data
      this.moduleAdvancedSettings = false;
      this.searchKeyword = null;
      this.focusSuggest = 0;
      this.$store.dispatch("filterElement", null);
    },
    async createModule() {
      this.$v.vector.$touch();

      if (this.$v.vector.$invalid) {
        this.$toasted.error(
          validator.moduleDesignError(this.$v.vector, "name")
        );
      } else {
        this.submitting = true;
        try {
          await this.$store.dispatch("addVector", { ...this.vector });
          const vectorId =
            this.$store.state.vector && this.$store.state.vector.id;
          const vectorName =
            this.$store.state.vector && this.$store.state.vector.name;
          this.$emit("module:created");
          this.$toasted.success(
            `${
              vectorName ? `Module: ${vectorName}` : "Module"
            } has been created.`
          );
          if (vectorId)
            this.$router.push({
              query: { id: commons.encodeids(vectorId) }
            });
        } catch (err) {
          console.error(err);
          this.$toasted.global.error({
            response: err.response,
            message: "Unable create module"
          });
        } finally {
          this.submitting = false;
        }
      }
    },
    cancelCreateModule() {
      this.$emit("cancel");
    },
    debounceInput: _.debounce(async function(target) {
      if (this.vector.name) {
        let vm = this;
        this.$store
          .dispatch("ai", {
            type: "emoji",
            text: this.vector.name
          })
          .then(function(response) {
            Vue.set(vm.vector, "emoji", response.data);
          });
      }
    }, 800),
    checkContent(fieldName) {
      let canvasFields = this.canvas[fieldName];

      if (!this.beta && fieldName === "standardActivities")
        canvasFields = canvasFields.filter(x => x.id !== "print");

      return (
        (canvasFields &&
          canvasFields.some(
            x =>
              (x.displayName &&
                x.displayName.toLowerCase().indexOf(this.getSearhKeyword) >
                  -1) ||
              (x.name &&
                x.name.toLowerCase().indexOf(this.getSearhKeyword) > -1)
          )) ||
        (this.systemDefault[fieldName] &&
          this.systemDefault[fieldName].some(
            x =>
              x.displayName &&
              x.displayName.toLowerCase().indexOf(this.getSearhKeyword) > -1
          ))
      );
    },
    /* workspace options */
    async loadWorkspaces(search) {
      let result = await this.$store.dispatch("searchWorkspace", {
        search: search || ""
      });
      this.workspaceOptions = result;
      if (
        this.$store.state.workspace &&
        !this.workspaceOptions.some(x => x.id == this.$store.state.workspace.id)
      ) {
        this.workspaceOptions.push(this.$store.state.workspace);
      }
    },
    async preloadWorkspaces(loading) {
      loading(true);
      await this.loadWorkspaces();
      loading(false);
    },
    searchWorkspace: _.debounce(async (loading, search, vm) => {
      await vm.loadWorkspaces(search);
      loading(false);
    }, 300),
    async fetchWorkspaceOptions({ searchText, loading }) {
      loading(true);
      await this.searchWorkspace(loading, searchText, this);
    },
    /* console element */
    async openConsoleElement(type, selected, params) {
      let vm = this;
      let item = { ...selected };

      if (!item.id && type.name !== "information") {
        item = {
          id: type.name === "menu" ? commons.nanoID() : commons.newUUID(),
          ...selected
        };

        if (type.name == "document") {
          item.version = 1.0;
        }
      }
      /* update */
      function set(result, save) {
        let items = vm.canvas[type.fieldName];

        if (!items) {
          items = [];
          // vm.$set(vm.canvas, type.fieldName, []);
          // items = vm.canvas[type.fieldName];
        }
        const findIndex = items.findIndex(x => x.id == result.id);
        if (findIndex > -1) {
          vm.$set(items, findIndex, result);
        } else if (type.name === "information") {
          let findDeep = _.findDeep(items, x => x.id == result.id, {
            childrenPath: "fields"
          });
          if (findDeep && findDeep.parent && findDeep.key) {
            vm.$set(findDeep.parent.fields, findDeep.key, result);
          } else {
            items.push(result);
          }
        } else {
          items.push(result);
        }
        /* information:push new field(s) to classification */
        if (
          type.name === "information" &&
          (result.type == 14 || result.type == 15)
        ) {
          commons.expandInformationFields(vm.canvas);
        }
        /* update canvas */
        vm.$set(vm.canvas, type.fieldName, items);
        /* compact classification */
        commons.compactClassification(vm.canvas);
        /* update vector canvas */
        vm.$store.dispatch("updateVectorCanvas", vm.canvas);
        /* save canvas */
        if (save) {
          vm.$emit("update");
        }
      }
      /* remove */
      function remove() {
        let items = vm.canvas[type.fieldName];
        const findIndex = items.findIndex(x => x.id == item.id);
        if (findIndex > -1) {
          items.splice(findIndex, 1);
        } else if (type.name === "information") {
          let findDeep = _.findDeep(items, x => x.id == item.id, {
            childrenPath: "fields"
          });
          if (findDeep && findDeep.parent && findDeep.key) {
            vm.$delete(findDeep.parent.fields, findDeep.key);
          }
        }
      }
      /* set alternative design */
      function setAlternativeDesign(canvas, items) {
        items.forEach(x => {
          if (x && x.type && x.extension) {
            if (x.type === "flow") {
              canvasDesign.setFlow(canvas, x.extension);
            }
          }
        });
      }
      if (type.name === "information") {
        if (item.system) {
          if (item.id === "title") {
            item.title = this.canvas.title;
          }
        } else if (!item.id) {
          item = {
            id: commons.generateFieldName(),
            fieldName: commons.generateFieldName(),
            ...selected
          };
        }
        const result = await this.$refs.ftsInformationDesignModal.show(
          item,
          null,
          continueFormData => {
            set(continueFormData);
            this.openConsoleElement(type);
          }
        );
        if (result != null && result != true) {
          if (result.system) {
            if (result.id === "title") {
              this.$set(this.canvas, "title", result.title);
            } else if (result.id == "documentId") {
              if (result.documentIdFormat) {
                this.$set(
                  this.vector,
                  "documentIdFormat",
                  result.documentIdFormat
                );
              } else {
                this.$delete(this.vector, "documentIdFormat");
              }
            }
          } else {
            set(result);
          }
        } else if (result == true) {
          remove();
        }
      } else if (type.name === "menu") {
        const result = await this.$refs.listingDesign.show(item, formData => {
          set(formData, true);
        });
        if (result != null && result != true) {
          set(result);
        } else if (result == true) {
          remove();
        }
      } else if (type.name == "document") {
        const result = await this.$refs.ftsDocumentDesignModal.show(
          item,
          formData => {
            set(formData, true);
          }
        );
        if (result != null && result != true) {
          set(result);
        } else if (result == true) {
          remove();
        }
      } else if (type.name === "activity") {
        const result = await this.$refs.ftsActivityDesignModal.show(
          item,
          /* save */
          (formData, alternativeDesign) => {
            let canvas = _.cloneDeep(this.$store.state.canvas);
            /* set activity */
            canvasDesign.setActivity(canvas, formData);
            /* set alternative design */
            if (alternativeDesign) {
              setAlternativeDesign(canvas, alternativeDesign);
            }
            /* compact classification */
            commons.compactClassification(canvas);
            /* dispatch state */
            this.$store.dispatch("updateVectorCanvas", canvas);
            /* save */
            this.$emit("update");
          },
          /* continue callback */
          (continueFormData, alternativeDesign) => {
            let canvas = _.cloneDeep(this.$store.state.canvas);
            /* set activity */
            canvasDesign.setActivity(canvas, continueFormData);
            /* set alternative design */
            if (alternativeDesign) {
              setAlternativeDesign(canvas, alternativeDesign);
            }
            /* compact classification */
            commons.compactClassification(canvas);
            /* dispatch state */
            this.$store.dispatch("updateVectorCanvas", canvas);
            /* open design */
            this.openConsoleElement(type);
          },
          /* navigate callback */
          (
            navigateFormData,
            navigateType,
            navigateData,
            navigateParams,
            alternativeDesign
          ) => {
            let canvas = _.cloneDeep(this.$store.state.canvas);
            /* set activity */
            canvasDesign.setActivity(canvas, navigateFormData);
            /* set alternative design */
            if (alternativeDesign) {
              setAlternativeDesign(canvas, alternativeDesign);
            }
            /* compact classification */
            commons.compactClassification(canvas);
            /* dispatch state */
            this.$store.dispatch("updateVectorCanvas", canvas);
            /* open design */
            setTimeout(() => {
              this.openConsoleElement(
                navigateType,
                navigateData,
                navigateParams
              );
            }, 500);
          },
          /* params */
          params
        );
        if (result != null && result != true) {
          let canvas = _.cloneDeep(this.$store.state.canvas);
          if (result.formData) {
            /* set activity */
            canvasDesign.setActivity(canvas, result.formData);
            /* set alternative */
            if (result.alternativeDesign) {
              setAlternativeDesign(canvas, result.alternativeDesign);
            }
          } else {
            /* set activity */
            canvasDesign.setActivity(canvas, result);
          }
          /* compact classification */
          commons.compactClassification(canvas);
          /* dispatch state */
          this.$store.dispatch("updateVectorCanvas", canvas);
        } else if (result == true) {
          remove();
        }
      } else if (type.name === "standardActivity" && item.id === "import") {
        const result = await this.$refs.ftsImportDesignModal.show(
          this.canvas.import || {}
        );
        if (result != null && result != true) {
          this.$set(this.canvas, "import", result);
        } else if (result == true) {
          this.$delete(this.canvas, "import");
        }
      } else if (type.name === "standardActivity" && item.id) {
        const result = await this.$refs.ftsStandardActivityDesignModal.show(
          item,
          formData => {
            set(formData, true);
          },
          (navigateFormData, navigateType, navigateData, navigateParams) => {
            set(navigateFormData);
            /* open design */
            setTimeout(() => {
              this.openConsoleElement(
                navigateType,
                navigateData,
                navigateParams
              );
            }, 500);
          }
        );
        if (result != null && result != true) {
          set(result);
        }
      } else if (type.name === "state") {
        const result = await this.$refs.ftsStateDesignModal.show(
          item,
          null,
          continueFormData => {
            set(continueFormData);
            this.openConsoleElement(type);
          },
          (navigateFormData, navigateType, navigateData, navigateParams) => {
            set(navigateFormData);
            /* open design */
            setTimeout(() => {
              this.openConsoleElement(
                navigateType,
                navigateData,
                navigateParams
              );
            }, 500);
          }
        );
        if (result != null && result != true) {
          set(result);
        } else if (result == true) {
          remove();
        }
      } else if (type.name === "flow") {
        const result = await this.$refs.ftsFlowDesignModal.show(
          item,
          formData => {
            set(formData, true);
          }
        );
        if (result != null && result != true) {
          set(result);
        } else if (result == true) {
          remove();
        }
      } else if (type.name === "action") {
        const result = await this.$refs.ftsActionDesignModal.show(
          item,
          formData => {
            set(formData, true);
          }
        );
        if (result != null && result != true) {
          set(result);
        } else if (result == true) {
          remove();
        }
      } else if (type.name === "logicService") {
        const result = await this.$refs.ftsLogicServiceDesignModal.show(
          item,
          formData => {
            set(formData, true);
          }
        );
        if (result != null && result != true) {
          set(result);
        } else if (result == true) {
          remove();
        }
      }
    },
    // automation
    getAutomation(type) {
      return this.$store.getters.vectorTriggers.filter(
        x => x.type == type && x.item == null
      );
    },
    getAutomationLabel(item) {
      if (item.description) return item.description;
      else if (item.notification) return "Notification";
      else if (item.actionType === "advanced") return "Advanced";
      else return "Automation";
    },
    async showAutomationDesignModal(item) {
      await this.$refs.ftsAutomationDesignModal.show(item);
    },
    addAutomation(element, actionType) {
      this.showAutomationDesignModal({
        item: null,
        type: element.fieldName,
        trigger: "execute",
        actionType: actionType
      });
    },
    /* subscription */
    async openSubscriptionDesignModal(item) {
      /* mock data */
      let subscriptions = [
        {
          id: "orADaBp-Ov",
          name: "when changed",
          audit: {
            updatedBy: "admin",
            updatedDate: "2022-01-25T09:27:36+08:00"
          },
          source: "zapier",
          active: true
        },
        {
          id: "rP2Ak8VGMS",
          name: "when due",
          audit: {
            updatedBy: "yanzheng.lam@gneysoftware.com",
            updatedDate: "2022-01-24T09:27:36+08:00"
          },
          source: "zapier",
          active: true
        },
        {
          id: "xq3f2vG2DV",
          name: "when changed to",
          audit: {
            updatedBy: "jiaxiang.tan@gneysoftware.com",
            updatedDate: "2022-01-23T01:27:36+08:00"
          },
          source: "integromat",
          active: false
        },
        {
          id: "qVDya8yUaV",
          name: "when changed from",
          audit: {
            updatedBy: "jiaxiang.tan@gneysoftware.com",
            updatedDate: "2022-01-22T17:27:36+08:00"
          },
          source: "integromat",
          active: true
        }
      ];
      /* to-do assign data */
      const res = await this.$refs.ftsSubscriptionDesignModal.show(
        subscriptions,
        {
          title: item.name || item.displayName
        }
      );
    },
    informationIcon(type) {
      return commons.informationIcon(type);
    },
    filter() {
      const container = document.getElementsByClassName("studio-content").length
        ? ".studio-content"
        : "body";
      VueScrollTo.scrollTo("#searchSuggestion", 500, {
        container: container,
        offset: -125
      });
      this.$store.dispatch("filterElement", this.getSearhKeyword);
    },
    clearFilter() {
      this.searchKeyword = null;
      this.focusSuggest = 0;
    },
    // Suggest
    restructureData(x) {
      let type = {
        name: x.section
      };
      let item = {};
      if (x.section === "information") {
        Object.assign(type, { fieldName: "information" });
        Object.assign(item, {
          displayName: x.displayName,
          type: x.type
        });
      } else if (x.section === "state") {
        Object.assign(type, { fieldName: "states" });
        Object.assign(item, {
          name: x.name,
          color: x.color
        });
      } else if (x.section === "activity") {
        Object.assign(type, { fieldName: "activities" });
        Object.assign(item, {
          name: x.name,
          emoji: x.emoji
        });
      }
      return { type: type, item: item };
    },
    submitSuggest() {
      if (this.suggestions && this.suggestions.length) {
        this.editSuggest(this.suggestions[this.focusSuggest]);
      } else if (this.filterCard.length === 1 && this.searchResult) {
        this.openConsoleElement(this.filterCard[0], this.searchResult);
      }
    },
    selectSuggest(ind) {
      this.focusSuggest = ind;
      setTimeout(() => {
        this.$refs.searchKeyword.focus();
      }, 100);
      this.submitSuggest();
    },
    switchSuggest() {
      if (this.suggestions && this.suggestions.length) {
        if (this.focusSuggest == this.suggestions.length - 1)
          this.focusSuggest = 0;
        else this.focusSuggest += 1;
      }
    },
    editSuggest(x) {
      let data = this.restructureData(x);
      this.openConsoleElement(data.type, data.item);
    },
    setSuggesMouseHover(x, visible) {
      if (visible) this.$set(x, "mouseHover", true);
      else this.$delete(x, "mouseHover");
    },
    deleteModule() {
      this.$emit("deleteModule", this.vector);
    },
    toggleNotificationPanel() {
      this.showNotificationPanel = !this.showNotificationPanel;
      if (this.showNotificationPanel) {
        this.moduleAdvancedSettings = false;
      }
    },
    toggleModuleSettings() {
      this.moduleAdvancedSettings = !this.moduleAdvancedSettings;
      if (this.moduleAdvancedSettings) {
        this.showNotificationPanel = false;
      }
    }
  },
  computed: {
    information() {
      return this.$store.getters.canvasInformation;
    },
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    beta() {
      return this.$store.state.beta;
    },
    workspace() {
      return this.$store.state.workspace || {};
    },
    getSearhKeyword() {
      if (this.searchKeyword) return this.searchKeyword.toLowerCase();
    },
    suggestions() {
      if (this.searchKeyword) return this.$store.state.suggestions;

      return [];
    },
    filterCard() {
      let cards = this.cards;
      if (this.getSearhKeyword) {
        return this.cards.filter(
          x =>
            x.name == "vector" ||
            x.displayName.toLowerCase().indexOf(this.getSearhKeyword) > -1 ||
            this.checkContent(x.fieldName)
        );
      }
      return cards;
    },
    searchResult() {
      let results = [];

      try {
        this.cards.forEach(x => {
          results = results.concat(
            this.canvas[x.fieldName].filter(
              c =>
                (c.displayName &&
                  c.displayName.toLowerCase().indexOf(this.getSearhKeyword) >
                    -1) ||
                (c.name &&
                  c.name.toLowerCase().indexOf(this.getSearhKeyword) > -1)
            )
          );
        });
      } catch (err) {
        console.warn(err);
      }

      if (results.length === 1 && this.filterCard.length === 1)
        return results[0];
    },
    cardGroup() {
      return [
        {
          name: "group1",
          group: this.filterCard.filter(
            x =>
              (x.name == "standardActivity" || x.name == "menu") &&
              (!x.beta || (this.beta && x.beta))
          )
        },
        {
          name: "group2",
          group: this.filterCard.filter(
            x =>
              (x.name == "information" ||
                x.name == "action" ||
                x.name == "document") &&
              (!x.beta || (this.beta && x.beta))
          )
        },
        {
          name: "group3",
          group: this.filterCard.filter(
            x =>
              (x.name == "activity" ||
                x.name == "state" ||
                x.name == "flow" ||
                x.name == "logicService") &&
              (!x.beta || (this.beta && x.beta))
          )
        }
      ];
    },
    moduleWorkspace: {
      get() {
        if (this.vector.ownerWorkspaceId) {
          let module = { id: this.vector.ownerWorkspaceId };
          if (this.workspace.id === this.vector.ownerWorkspaceId)
            module.name = this.$store.state.workspace.name;
          else module.name = this.vector.ownerWorkspace;

          return module;
        }
        return null;
      },
      set(val) {
        if (val) {
          this.vector.ownerWorkspaceId = val.id;
          this.vector.ownerWorkspace = val.name;
        } else {
          this.vector.ownerWorkspaceId = null;
          this.vector.ownerWorkspace = null;
        }
      }
    },
    systemDefault() {
      const systemDefault = {
        information: [
          {
            id: "documentId",
            displayName: "Document ID",
            type: 0,
            description:
              "Auto-generated unique document ID combination of prefix and serial number. eg. INV00245. Click to change the format, or to reset serial number",
            system: true
          },
          {
            id: "title",
            displayName: "Title",
            type: 0,
            description:
              "Auto-generated unique title/subject for an entry. Click to change the format",
            system: true
          },
          {
            id: "createdBy",
            displayName: "Created By",
            type: 19,
            description: "Users who created an entry",
            system: true
          },
          {
            id: "date",
            displayName: "Created Date",
            type: 5,
            description: "Date and time when an entry is created",
            system: true
          },
          {
            id: "lastChangeBy",
            displayName: "Last Change By",
            type: 19,
            description: "Users who did the last changed of an entry",
            system: true
          },
          {
            id: "lastChangeDate",
            displayName: "Last Change Date",
            type: 5,
            description: "Date and time of an entry last changed",
            system: true
          }
        ]
      };
      if (this.vector && this.vector.isUserCollection)
        return {
          ...systemDefault,
          information: [
            ...systemDefault.information,
            {
              id: "username",
              displayName: "Username",
              type: 19,
              description: "Username of user who uses this entry as profile",
              system: true
            }
          ]
        };
      else return systemDefault;
    }
  },
  watch: {
    vectorProps: function() {
      if (
        (this.vectorId && this.vector && !this.vector.id) ||
        (this.vector && this.vector.id != this.vectorId)
      ) {
        this.vector = this.vectorProps;
        this.reset();

        this.$nextTick(() => {
          if (this.$refs.searchKeyword && this.vectorId)
            this.$refs.searchKeyword.focus();
          else if (this.$refs.moduleName) this.$refs.moduleName.$el.focus();
        });
      }
    },
    canvasProps: function() {
      this.canvas = this.canvasProps;
    },
    canvas: async function(val) {
      if (val == null) return;

      Vue.set(val, "documents", val.documents);
      Vue.set(val, "actions", val.actions);
      for (var card of this.cards) {
        if (!val[card.fieldName]) {
          Vue.set(val, card.fieldName, []);
        }
      }

      let vm = this;

      this.$store.dispatch("loadCanvas", {
        canvas: val,
        baseServer: this.baseServer,
        token: this.token,
        vector: this.vectorId
      });

      if (
        vm.$store.state.workspace &&
        !vm.workspaceOptions.some(x => x.id == vm.$store.state.workspace.id)
      ) {
        vm.workspaceOptions.push(vm.$store.state.workspace);
      }

      let path = this.$route.path;
      let params = path.split("/");
      if (
        (params.length && params[1] == "uc") ||
        (params.length && params[1] == "ucd")
      ) {
        let card = this.sections[1].cards.find(x => x.fieldName == params[2]);
        if (card) {
          let item = this.canvas[card.fieldName].find(x => x.id == params[3]);

          if (params.length && params[1] == "uc") {
            // to:do open modal via route path
          } else {
            window.eventBus.$emit("workDesignData", {
              item: item
            });
          }
        }
      }
    }
  },
  mounted() {
    /* scroll to */
    this.$emit("scrollTo", { x: 0, y: 0 });
    /* set canvas */
    this.canvas = this.canvasProps;
    /* set vector */
    if (this.vectorId) {
      this.vector = this.vectorProps;
    }
    /* load canvas */
    this.$store.dispatch("loadCanvas", {
      canvas: this.canvas,
      baseServer: this.baseServer,
      token: this.token,
      vector: this.vectorId
    });
    /* select workspace */
    if (this.workspaceId != null) {
      this.$store.dispatch("selectWorkspace", { id: this.workspaceId });
    }
    /* scroll event */
    window.eventBus.$on("ftsConsoleOnScroll", () => {
      if (this.vectorId) {
        const el = this.$refs.consoleSearchInput;

        if (
          el &&
          el.getBoundingClientRect() &&
          el.getBoundingClientRect().top === 55
        ) {
          el.classList.add("sticked");
        } else if (el && el.classList && el.classList.contains("sticked")) {
          el.classList.remove("sticked");
        }
      }
    });
    /**
     * route query
     * t - design type
     * tid - design ID
     */
    const designName = this.$route.query.t;
    const designId = this.$route.query.tid;
    if (
      designName &&
      ["standardActivity", "activity", "menu"].indexOf(designName) > -1
    ) {
      if (designId) {
        try {
          const type = this.cards.find(x => x.name === designName);
          if (type) {
            let selected = this.canvas[type.fieldName].find(
              x => x.id === designId
            );
            if (designName === "standardActivity") {
              if (
                ["create", "edit", "view"].indexOf(designId) > -1 &&
                !selected.design
              ) {
                selected = this.canvas[type.fieldName].find(
                  x => x.id === "createEditView"
                );
              } else if (designId === "quickView") {
                selected = this.canvas[type.fieldName].find(
                  x => x.id === "view"
                );
                if (!selected.design)
                  selected = this.canvas[type.fieldName].find(
                    x => x.id === "createEditView"
                  );
              }
            }
            if (selected) {
              this.openConsoleElement(type, selected);
              return;
            }
          }
        } catch (err) {
          console.warn("FTSConsole", err);
        }
      }
    }
    /* autofocus search input */
    this.$nextTick(() => {
      if (this.$refs.searchKeyword && this.vectorId)
        this.$refs.searchKeyword.focus();
      else if (this.$refs.moduleName) this.$refs.moduleName.$el.focus();
    });
  },
  beforeDestroy() {
    window.eventBus.$off("ftsConsoleOnScroll");
  }
};
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-console {
  &.mh {
    min-height: calc(100vh + 560px);
  }
  /* section */
  .console-section {
    display: flex;
    .console-section-wrapper {
      flex: 1 1 33.333333%;
      max-width: 33.333333%;
      display: flex;
      flex-direction: column;
    }
  }
  /* card */
  .console-card {
    margin: 0.875rem;
    border-radius: 0.25rem;
    background: var(--fts-card-background);
    &:not(.inactive) {
      box-shadow: var(--fts-elevation-3);
    }
    &.inactive {
      .console-card-header {
        border-bottom-left-radius: inherit;
        border-bottom-right-radius: inherit;
        &:after {
          content: "";
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          border-radius: inherit;
          background: var(--fts-disabled);
        }
      }
    }
    .console-card-header {
      position: relative;
      padding: 1rem;
      border-top-left-radius: inherit;
      border-top-right-radius: inherit;
      background-size: cover;
      background-image: url("../../assets/images/studio/mesh-gradient.png");
      z-index: 1;
      .console-card-title-wrapper {
        display: flex;
        align-items: center;
        margin-bottom: 0.75rem;
        .console-card-title {
          flex: 1;
          font-weight: 700;
          font-size: 2rem;
          line-height: 3rem;
          color: var(--fts-primary);
        }
        .console-card-action-create {
          margin-left: 1rem;
        }
      }
      .console-card-description {
        font-size: 0.875rem;
        line-height: 1.25rem;
        color: var(--fts-form-label);
        cursor: default;
        .console-card-description-link {
          color: var(--fts-primary);
          text-decoration: none;
        }
      }
    }
    .console-card-body {
      .console-card-content {
        display: flex;
        align-items: center;
        padding: 0.575rem 0.75rem;
      }
      .console-card-element {
        max-height: 600px;
        overflow: auto;
        padding: 1rem 0;
      }
      .console-card-body__footer {
        display: flex;
        align-items: center;
        padding: 0.75rem 1.15rem;
        border-top: 1px solid var(--fts-gray-3);
      }
      .console-card-content-actions {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        margin-left: 1rem;
        .console-card-event-btn {
          &:not(:first-child) {
            margin-left: 0.5rem;
          }
          &:not(:last-child) {
            margin-right: 0.5rem;
          }
        }
      }
    }
    /* automation */
    .console-card-automation-container {
      @include flex-gap($display: flex, $gap: 0.375rem);
    }
    .console-card-automation-container + .console-card-audit {
      margin-top: 0.375rem;
    }
    /* audit */
    .console-card-audit {
      display: flex;
      align-items: center;
      font-size: 0.75rem;
      line-height: 1rem;
      .console-card-audit-by {
        margin-right: 0.375rem;
        color: var(--fts-gray-5);
        white-space: nowrap;
      }
      .console-card-audit-date {
        margin-left: 0.75rem;
        color: var(--fts-gray-5);
        white-space: nowrap;
      }
    }
  }
  /* module form */
  .console-card-module {
    .console-card-module-info {
      display: flex;
      align-items: center;
      margin-bottom: 0.5rem;
      .console-card-module-emoji {
        margin-right: 0.575rem;
      }
      .console-card-module-name {
        width: auto !important;
        padding: 0.375rem 0.75rem;
        font-size: 2.5rem;
        font-weight: 700;
        color: var(--fts-primary);
        &:focus {
          box-shadow: 0 0 20px 10px rgba($color: #fff, $alpha: 0.08),
            0 1px 20px 8px rgba($color: #fff, $alpha: 0.08);
          background: rgba($color: #eee, $alpha: 0.25);
          border-radius: 0.5rem;
        }
      }
      .console-card-module-tag {
        margin-top: 0.75rem;
        margin-left: 0.575rem;
        padding: 0.1rem 0.175rem;
        font-size: 0.675rem;
        border-radius: 0.25rem;
        background: var(--drawer-secondary-background);
        color: var(--drawer-secondary);
      }
    }
    .console-card-module-description {
      margin-top: 0.75rem;
      margin-bottom: 1rem;
      font-size: 0.875rem;
      line-height: 1.25rem;
      color: var(--fts-form-label);
    }
    .console-card-toggle {
      position: absolute;
      top: 0.75rem;
      right: 0.75rem;
      > *:not(:first-child) {
        margin-left: 0.75rem;
      }
      .console-card-toggle-label {
        display: flex;
        align-items: center;
        color: var(--fts-form-label);
        .label-helper {
          margin-left: 0.375rem;
          font-size: 0.875rem;
          cursor: default;
        }
      }
    }
    .console-card-module-form {
      padding: 0.75rem;
      .form-divider {
        margin-top: 1rem;
      }
    }
    .console-card-create-action {
      padding: 1rem;
    }
  }
  /* search */
  .console-search {
    position: sticky;
    top: 55px;
    padding: 0.75rem 0.875rem;
    transition: all 0.3s ease-in-out;
    z-index: 50;
    &.sticked {
      @include backdrop-background($color: #fff, $alpha: 0.5, $blur: 10px);
    }
  }
  /* suggestion */
  .console-suggest {
    .console-suggest-content {
      padding: 0.875rem;
      .console-suggest-title {
        margin-bottom: 0.275rem;
        font-weight: 500;
        font-size: 1.15rem;
        color: $grayColor;
      }
      .console-suggest-desc {
        font-size: 0.875rem;
        color: var(--fts-gray-5);
      }
      .console-suggest-results {
        display: flex;
        flex-wrap: wrap;
        margin: -0.5rem;
        padding-top: 1rem;
        .console-suggest-result {
          max-width: 20%;
          flex: 1 1 20%;
          padding: 0.5rem;
          .console-suggest-card {
            padding: 0.75rem;
            border: 2px solid var(--fts-gray-3);
            border-radius: 0.25rem;
            background: var(--fts-card-background);
            transition: all 0.3s ease-in-out;
            &.focused {
              @include theme() {
                box-shadow: 0 2px 3px 0
                  rgba($color: theme-get("fts-primary"), $alpha: 0.12);
              }
              border-color: var(--fts-primary);
            }
            .console-suggest-card-header {
              min-width: 0;
              display: flex;
              align-items: center;
              font-size: 0.875rem;
              color: var(--fts-gray-5);
              .console-suggest-card-check {
                margin-right: 0.575rem;
              }
              .console-suggest-card-title {
                @include text-overflow-hidden;
                text-transform: capitalize;
              }
            }
            .console-suggest-card-body {
              position: relative;
              min-height: 28px;
              margin-top: 0.575rem;
              .console-suggest-element {
                display: flex;
                align-items: center;
                font-size: 1.275rem;
                .console-suggest-element-icon {
                  margin-right: 0.575rem;
                }
                .console-suggest-element-name {
                  @include text-overflow-hidden;
                  color: var(--fts-text);
                }
              }
              .console-suggest-state {
                > div {
                  font-size: 1rem;
                }
              }
              .console-suggest-card-action {
                @include position(
                  $top: 50%,
                  $right: -0.175rem,
                  $transform: translateY(-50%)
                );
              }
            }
          }
        }
      }
    }
  }
  /* no result */
  .console-no-result {
    @include flex-center-middle;
    flex-direction: column;
    min-height: 150px;
    .no-result-label {
      font-size: 1.15rem;
      font-weight: 600;
    }
    .no-result-desc {
      margin-top: 0.575rem;
      font-size: 0.875rem;
    }
  }
  /* import */
  .console-import {
    display: flex;
    align-items: center;
    .console-import__auth {
      margin-left: 0.75rem;
    }
  }
}
</style>